import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { 
  BarChart3, 
  TrendingUp, 
  Target, 
  Building2, 
  Database, 
  Cpu,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
  Search,
  Filter,
  Download,
  Calendar,
  Users,
  Award,
  Layers,
  Activity,
  PieChart,
  LineChart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KPIScorecardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailSearched, setEmailSearched] = useState(false);
  const [userAssessments, setUserAssessments] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('Manufacturing');
  const [industryBenchmarks, setIndustryBenchmarks] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDigitalAssessment, setSelectedDigitalAssessment] = useState(null);
  const [selectedDataAssessment, setSelectedDataAssessment] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    fetchSummaryData();
  }, []);

  useEffect(() => {
    if (selectedIndustry) {
      fetchIndustryBenchmarks(selectedIndustry);
    }
  }, [selectedIndustry]);

  useEffect(() => {
    if (selectedDigitalAssessment && selectedDataAssessment) {
      fetchComparison(selectedDigitalAssessment.id, selectedDataAssessment.id);
    }
  }, [selectedDigitalAssessment, selectedDataAssessment]);

  const fetchSummaryData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/kpi-scorecard/summary`);
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssessments = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/kpi-scorecard/assessments/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setUserAssessments(data);
        setEmailSearched(true);
        // Auto-select latest assessments if available
        if (data.digital_maturity_assessments?.length > 0) {
          setSelectedDigitalAssessment(data.digital_maturity_assessments[0]);
        }
        if (data.data_maturity_assessments?.length > 0) {
          setSelectedDataAssessment(data.data_maturity_assessments[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching user assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryBenchmarks = async (industry) => {
    try {
      const response = await fetch(`${API_URL}/api/kpi-scorecard/industry-benchmarks/${encodeURIComponent(industry)}`);
      if (response.ok) {
        const data = await response.json();
        setIndustryBenchmarks(data);
      }
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
    }
  };

  const fetchComparison = async (digitalId, dataId) => {
    try {
      const response = await fetch(`${API_URL}/api/kpi-scorecard/comparison/${digitalId}/${dataId}`);
      if (response.ok) {
        const data = await response.json();
        setComparisonData(data);
      }
    } catch (error) {
      console.error('Error fetching comparison:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-emerald-600';
    if (score >= 3) return 'text-blue-600';
    if (score >= 2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 4) return 'bg-emerald-100';
    if (score >= 3) return 'bg-blue-100';
    if (score >= 2) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const ScoreGauge = ({ score, maxScore = 5, label, size = 'normal' }) => {
    const percentage = (score / maxScore) * 100;
    const circumference = 2 * Math.PI * 40;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    const sizeClass = size === 'large' ? 'w-32 h-32' : 'w-24 h-24';
    
    return (
      <div className="flex flex-col items-center">
        <div className={`relative ${sizeClass}`}>
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="40"
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="50%"
              cy="50%"
              r="40"
              stroke={score >= 4 ? '#10b981' : score >= 3 ? '#3b82f6' : score >= 2 ? '#f59e0b' : '#ef4444'}
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`font-bold ${size === 'large' ? 'text-2xl' : 'text-xl'} ${getScoreColor(score)}`}>
              {score?.toFixed(1)}
            </span>
            <span className="text-xs text-gray-500">/ {maxScore}</span>
          </div>
        </div>
        {label && <span className="mt-2 text-sm text-gray-600 text-center">{label}</span>}
      </div>
    );
  };

  const ComparisonBar = ({ digital, data, label }) => {
    const maxScore = 5;
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="flex gap-4 text-sm">
            <span className={`font-semibold ${getScoreColor(digital)}`}>{digital?.toFixed(1) || 'N/A'}</span>
            <span className="text-gray-400">vs</span>
            <span className={`font-semibold ${getScoreColor(data)}`}>{data?.toFixed(1) || 'N/A'}</span>
          </div>
        </div>
        <div className="flex gap-1 h-4">
          <div className="flex-1 bg-gray-100 rounded-l overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-700"
              style={{ width: `${(digital / maxScore) * 100}%` }}
            />
          </div>
          <div className="flex-1 bg-gray-100 rounded-r overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-400 to-purple-600 transition-all duration-700"
              style={{ width: `${(data / maxScore) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'comparison', label: 'Assessment Comparison', icon: BarChart3 },
    { id: 'benchmarks', label: 'Industry Benchmarks', icon: Target },
    { id: 'kpis', label: 'KPI Library', icon: Layers }
  ];

  if (loading && !summaryData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 py-16">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-white/80 text-sm mb-6">
              <Activity className="w-4 h-4" />
              Unified Maturity Intelligence
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              KPI Scorecard Dashboard
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Compare your Digital and Data Maturity assessments side by side with industry benchmarks
            </p>
          </div>
          
          {/* Quick Stats */}
          {summaryData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{summaryData.assessments?.total || 0}</div>
                <div className="text-white/60 text-sm">Total Assessments</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{summaryData.industries?.digital_maturity || 0}</div>
                <div className="text-white/60 text-sm">Digital Industries</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{summaryData.industries?.data_maturity || 0}</div>
                <div className="text-white/60 text-sm">Data Industries</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{summaryData.kpis?.common_data_kpis || 0}+</div>
                <div className="text-white/60 text-sm">KPI Metrics</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Find Your Assessments
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && fetchUserAssessments()}
                  data-testid="email-search-input"
                />
              </div>
            </div>
            <button
              onClick={fetchUserAssessments}
              disabled={!email || loading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="search-assessments-btn"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Search
            </button>
          </div>
          
          {emailSearched && userAssessments && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-600">Digital Assessments:</span>
                  <span className="font-semibold text-blue-600">{userAssessments.total_digital}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-600">Data Assessments:</span>
                  <span className="font-semibold text-purple-600">{userAssessments.total_data}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* User's Latest Assessments */}
            {userAssessments && (userAssessments.total_digital > 0 || userAssessments.total_data > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Latest Digital Assessment */}
                {userAssessments.digital_maturity_assessments?.[0] && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Cpu className="w-6 h-6 text-white/80" />
                          <h3 className="text-lg font-semibold text-white">Digital Maturity</h3>
                        </div>
                        <span className="text-sm text-white/70">Latest Assessment</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-gray-500 text-sm">Company</p>
                          <p className="font-semibold text-gray-900">{userAssessments.digital_maturity_assessments[0].company_name}</p>
                        </div>
                        <ScoreGauge 
                          score={userAssessments.digital_maturity_assessments[0].overall_score} 
                          label="Score"
                        />
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreBgColor(userAssessments.digital_maturity_assessments[0].overall_score)} ${getScoreColor(userAssessments.digital_maturity_assessments[0].overall_score)}`}>
                          {userAssessments.digital_maturity_assessments[0].overall_maturity_level}
                        </span>
                        <button
                          onClick={() => navigate('/digital-maturity')}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                        >
                          View Details <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Latest Data Assessment */}
                {userAssessments.data_maturity_assessments?.[0] && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Database className="w-6 h-6 text-white/80" />
                          <h3 className="text-lg font-semibold text-white">Data Maturity</h3>
                        </div>
                        <span className="text-sm text-white/70">Latest Assessment</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-gray-500 text-sm">Company</p>
                          <p className="font-semibold text-gray-900">{userAssessments.data_maturity_assessments[0].company_name}</p>
                        </div>
                        <ScoreGauge 
                          score={userAssessments.data_maturity_assessments[0].overall_score} 
                          label="Score"
                        />
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreBgColor(userAssessments.data_maturity_assessments[0].overall_score)} ${getScoreColor(userAssessments.data_maturity_assessments[0].overall_score)}`}>
                          {userAssessments.data_maturity_assessments[0].maturity_level}
                        </span>
                        <button
                          onClick={() => navigate('/data-maturity')}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
                        >
                          View Details <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No Assessments CTA */}
            {(!userAssessments || (userAssessments.total_digital === 0 && userAssessments.total_data === 0)) && emailSearched && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assessments Found</h3>
                <p className="text-gray-500 mb-6">
                  We couldn't find any assessments for this email. Start your maturity journey today!
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => navigate('/digital-maturity')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all flex items-center gap-2"
                  >
                    <Cpu className="w-5 h-5" />
                    Start Digital Assessment
                  </button>
                  <button
                    onClick={() => navigate('/data-maturity')}
                    className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-all flex items-center gap-2"
                  >
                    <Database className="w-5 h-5" />
                    Start Data Assessment
                  </button>
                </div>
              </div>
            )}

            {/* Recent Platform Assessments */}
            {summaryData?.recent_assessments && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-blue-600" />
                    Recent Digital Assessments
                  </h3>
                  <div className="space-y-3">
                    {summaryData.recent_assessments.digital?.map((assessment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{assessment.company_name}</p>
                          <p className="text-sm text-gray-500">{assessment.industry}</p>
                        </div>
                        <div className={`font-semibold ${getScoreColor(assessment.overall_score)}`}>
                          {assessment.overall_score?.toFixed(1)}/5
                        </div>
                      </div>
                    ))}
                    {(!summaryData.recent_assessments.digital || summaryData.recent_assessments.digital.length === 0) && (
                      <p className="text-gray-500 text-center py-4">No recent assessments</p>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-600" />
                    Recent Data Assessments
                  </h3>
                  <div className="space-y-3">
                    {summaryData.recent_assessments.data?.map((assessment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{assessment.company_name}</p>
                          <p className="text-sm text-gray-500">{assessment.industry}</p>
                        </div>
                        <div className={`font-semibold ${getScoreColor(assessment.overall_score)}`}>
                          {assessment.overall_score?.toFixed(1)}/5
                        </div>
                      </div>
                    ))}
                    {(!summaryData.recent_assessments.data || summaryData.recent_assessments.data.length === 0) && (
                      <p className="text-gray-500 text-center py-4">No recent assessments</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comparison Tab */}
        {activeTab === 'comparison' && (
          <div className="space-y-6">
            {userAssessments && (userAssessments.total_digital > 0 || userAssessments.total_data > 0) ? (
              <>
                {/* Assessment Selectors */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Cpu className="w-5 h-5 text-blue-600" />
                      Select Digital Assessment
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {userAssessments.digital_maturity_assessments?.map((assessment, idx) => (
                        <button
                          key={assessment.id || idx}
                          onClick={() => setSelectedDigitalAssessment(assessment)}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            selectedDigitalAssessment?.id === assessment.id
                              ? 'bg-blue-50 border-2 border-blue-500'
                              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">{assessment.company_name}</p>
                              <p className="text-sm text-gray-500">{assessment.industry}</p>
                            </div>
                            <span className={`font-semibold ${getScoreColor(assessment.overall_score)}`}>
                              {assessment.overall_score?.toFixed(1)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Database className="w-5 h-5 text-purple-600" />
                      Select Data Assessment
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {userAssessments.data_maturity_assessments?.map((assessment, idx) => (
                        <button
                          key={assessment.id || idx}
                          onClick={() => setSelectedDataAssessment(assessment)}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            selectedDataAssessment?.id === assessment.id
                              ? 'bg-purple-50 border-2 border-purple-500'
                              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">{assessment.company_name}</p>
                              <p className="text-sm text-gray-500">{assessment.industry}</p>
                            </div>
                            <span className={`font-semibold ${getScoreColor(assessment.overall_score)}`}>
                              {assessment.overall_score?.toFixed(1)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Comparison Results */}
                {comparisonData && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <BarChart3 className="w-6 h-6 text-gray-700" />
                      Side-by-Side Comparison
                    </h3>

                    {/* Overall Scores */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                        <Cpu className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-blue-600 font-medium">Digital Maturity</p>
                        <p className="text-3xl font-bold text-blue-700 mt-2">
                          {comparisonData.overall_scores?.digital?.score?.toFixed(1)}
                        </p>
                        <p className="text-sm text-blue-600 mt-1">{comparisonData.overall_scores?.digital?.level}</p>
                      </div>
                      
                      <div className="flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-sm text-gray-500 mb-2">Gap Analysis</p>
                          {comparisonData.overall_scores?.digital?.score && comparisonData.overall_scores?.data?.score && (
                            <div className={`text-2xl font-bold ${
                              comparisonData.overall_scores.digital.score > comparisonData.overall_scores.data.score
                                ? 'text-blue-600' : 'text-purple-600'
                            }`}>
                              {Math.abs(comparisonData.overall_scores.digital.score - comparisonData.overall_scores.data.score).toFixed(2)}
                              {comparisonData.overall_scores.digital.score > comparisonData.overall_scores.data.score ? (
                                <ArrowUpRight className="w-6 h-6 inline ml-1 text-blue-500" />
                              ) : comparisonData.overall_scores.digital.score < comparisonData.overall_scores.data.score ? (
                                <ArrowDownRight className="w-6 h-6 inline ml-1 text-purple-500" />
                              ) : (
                                <Minus className="w-6 h-6 inline ml-1 text-gray-400" />
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-1">point difference</p>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                        <Database className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                        <p className="text-sm text-purple-600 font-medium">Data Maturity</p>
                        <p className="text-3xl font-bold text-purple-700 mt-2">
                          {comparisonData.overall_scores?.data?.score?.toFixed(1)}
                        </p>
                        <p className="text-sm text-purple-600 mt-1">{comparisonData.overall_scores?.data?.level}</p>
                      </div>
                    </div>

                    {/* Dimension Comparison */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Dimension Comparison</h4>
                      <div className="flex gap-6 mb-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gradient-to-r from-blue-400 to-blue-600 rounded" />
                          <span className="text-gray-600">Digital Maturity</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gradient-to-r from-purple-400 to-purple-600 rounded" />
                          <span className="text-gray-600">Data Maturity</span>
                        </div>
                      </div>
                      {comparisonData.dimension_comparison?.map((dim, idx) => (
                        <ComparisonBar
                          key={idx}
                          digital={dim.digital?.score}
                          data={dim.data?.score}
                          label={dim.label}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assessments to Compare</h3>
                <p className="text-gray-500 mb-4">
                  Search for your assessments using your email address to see comparison data.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Target className="w-6 h-6 text-emerald-600" />
                  Industry Benchmarks
                </h3>
                <div className="flex items-center gap-3">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    data-testid="industry-select"
                  >
                    {industryBenchmarks?.available_digital_industries?.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
              </div>

              {industryBenchmarks && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Digital Maturity Benchmarks */}
                  <div className="border border-blue-100 rounded-xl p-6 bg-gradient-to-br from-blue-50/50 to-white">
                    <div className="flex items-center gap-3 mb-4">
                      <Cpu className="w-6 h-6 text-blue-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Digital Maturity</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Industry Average</p>
                        <p className="text-2xl font-bold text-blue-600">{industryBenchmarks.digital_maturity?.benchmark_avg?.toFixed(1)}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Top Performers</p>
                        <p className="text-2xl font-bold text-emerald-600">{industryBenchmarks.digital_maturity?.benchmark_top?.toFixed(1)}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">Process Areas ({Object.keys(industryBenchmarks.digital_maturity?.process_areas || {}).length})</p>
                      {Object.entries(industryBenchmarks.digital_maturity?.process_areas || {}).slice(0, 4).map(([area, data]) => (
                        <div key={area} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-sm text-gray-700">{area}</span>
                          <span className="text-sm font-medium text-blue-600">{data.kpi_count} KPIs</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Data Maturity Benchmarks */}
                  <div className="border border-purple-100 rounded-xl p-6 bg-gradient-to-br from-purple-50/50 to-white">
                    <div className="flex items-center gap-3 mb-4">
                      <Database className="w-6 h-6 text-purple-600" />
                      <h4 className="text-lg font-semibold text-gray-900">Data Maturity</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Industry Average</p>
                        <p className="text-2xl font-bold text-purple-600">{industryBenchmarks.data_maturity?.benchmark_avg?.toFixed(1)}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Top Performers</p>
                        <p className="text-2xl font-bold text-emerald-600">{industryBenchmarks.data_maturity?.benchmark_top?.toFixed(1)}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700">Process Areas ({Object.keys(industryBenchmarks.data_maturity?.process_areas || {}).length})</p>
                      {Object.entries(industryBenchmarks.data_maturity?.process_areas || {}).slice(0, 4).map(([area, data]) => (
                        <div key={area} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-sm text-gray-700">{area}</span>
                          <span className="text-sm font-medium text-purple-600">{data.kpi_count} KPIs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* KPI Library Tab */}
        {activeTab === 'kpis' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Digital Maturity KPIs */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Cpu className="w-5 h-5" />
                    Digital Maturity KPIs
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">33 Industries • 4 Process Areas Each</p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {industryBenchmarks?.digital_maturity?.process_areas && Object.entries(industryBenchmarks.digital_maturity.process_areas).map(([area, data]) => (
                      <div key={area} className="border border-gray-100 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-gray-900">{area}</h4>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{data.kpi_count} KPIs</span>
                        </div>
                        <div className="space-y-2">
                          {data.kpis?.map((kpi, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                              <span className="text-gray-700">{kpi.name}</span>
                              <span className="text-blue-600 font-medium">
                                Avg: {kpi.benchmark_avg} | Top: {kpi.benchmark_top} {kpi.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate('/digital-maturity')}
                    className="w-full mt-4 py-3 text-blue-600 font-medium hover:bg-blue-50 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    Start Digital Assessment <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Data Maturity KPIs */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Data Maturity KPIs
                  </h3>
                  <p className="text-purple-100 text-sm mt-1">22 Industries • 3-4 Process Areas Each</p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {industryBenchmarks?.data_maturity?.process_areas && Object.entries(industryBenchmarks.data_maturity.process_areas).map(([area, data]) => (
                      <div key={area} className="border border-gray-100 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-gray-900">{area}</h4>
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">{data.kpi_count} KPIs</span>
                        </div>
                        <div className="space-y-2">
                          {data.kpis?.map((kpi, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                              <span className="text-gray-700">{kpi.name}</span>
                              <span className="text-purple-600 font-medium">
                                Avg: {kpi.benchmark_avg} | Top: {kpi.benchmark_top} {kpi.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate('/data-maturity')}
                    className="w-full mt-4 py-3 text-purple-600 font-medium hover:bg-purple-50 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    Start Data Assessment <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default KPIScorecardPage;
